import React, { useRef } from 'react';
import { IPresentationListing } from '../../../../entities-generated/models/presentation/presentation-listing';
import { IPresentationListingDisplayModel } from '../../entities/listing-display-model';
import { BwjPageHeader } from '../../../../framework/components/bwj-page-header';
import { xlate } from '../../../../framework/utils/i18n';
import { AppRoutes } from '../../../app-routes';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { PresentationListingComponent } from '../../components/presentation-listing';
import { BwjConfirmationDialog, IConfirmationDialogDisplayProps } from '../../../../framework/components/bwj-confirmation-dialog';
import { IShowManageSubscriptionModalArgs, ManageSubscriptionModal } from '../../components/manage-subscription-modal';
import { BwjAnnouncementBanner } from '../../../../framework/components/bwj-announcement';
import { IShowAnnouncementBannerArgs } from '../../../../framework/components/bwj-announcement/props';
import { IBwjFormOption } from '../../../../framework/entities/bwj-form-option';
import { BwjInput } from '../../../../framework/entities/bwj-input';
import { BwjGrid, Col, Row } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { BwjSingleSelectField } from '../../../../framework/components/bwj-select-field/single';
import { DiscussionListingMenuPanel, ILaunchDiscussionListingMenuPanelArgs } from '../../components/discussion-menu-panel';
import { CloneDiscussionPlanModal } from '../../components/clone-discussion-modal';

interface IUIProps {
  presentations: IPresentationListingDisplayModel[];
  orderingOptionInput: BwjInput<IBwjFormOption<'abcASC' | 'abcDESC' | 'chronASC' | 'chronDESC'>>;
  orderingOptions: IBwjFormOption<'abcASC' | 'abcDESC' | 'chronASC' | 'chronDESC'>[];
  onOrderingOptionUpdate: (option: IBwjFormOption<'abcASC' | 'abcDESC' | 'chronASC' | 'chronDESC'>) => void;
  onWillOpenContextMenu: (presentation: IPresentationListing, usePanel: boolean) => void;
  onWillCloseContextMenu: (presentation: IPresentationListing) => void;
  onContextMenuOpened: (presentation: IPresentationListing) => void;
  onContextMenuClosed: (presentation: IPresentationListing) => void;
  onCreatePresentation: () => void;
  onEditPresentation: (presentation: IPresentationListing) => void;
  onCloneDiscussionRequested: (presentation: IPresentationListing) => void;
  onCreateDiscussionClone: () => void;
  onBeginPresentation: (presentation: IPresentationListing) => void;
  onEndPresentation: (presentation: IPresentationListing) => void;
  onDeletePresentation: (presentation: IPresentationListing) => void;
  confirmDeleteDisplay: IConfirmationDialogDisplayProps | undefined;
  showManageSubscriptionModalArgs: IShowManageSubscriptionModalArgs | undefined;
  invokeShowAnnouncement: IShowAnnouncementBannerArgs | undefined;
  launchMenuPanelArgs: ILaunchDiscussionListingMenuPanelArgs | undefined;
  launchCloneDiscussionModal: {} | undefined;
}

export const UI: React.FunctionComponent<IUIProps> = (props) => {
  const sectionRulerRef = useRef<HTMLDivElement | null>(null);

  const onWillOpenContextMenu = (presentation: IPresentationListing) => {
    const sectionRuler = sectionRulerRef.current;
    const usePanel = !!sectionRuler && sectionRuler.clientWidth < 600;

    props.onWillOpenContextMenu(presentation, usePanel);
  };

  return (
    <>
      <BwjConfirmationDialog displayProps={props.confirmDeleteDisplay} closeButtonAriaLabel={xlate('Close dialog')} />
      <ManageSubscriptionModal showModalArgs={props.showManageSubscriptionModalArgs} />
      <CloneDiscussionPlanModal
        launchModal={props.launchCloneDiscussionModal}
        onConfirm={props.onCreateDiscussionClone}
      />
      <DiscussionListingMenuPanel
        launchPanel={props.launchMenuPanelArgs}
        onEditPresentation={props.onEditPresentation}
        onClonePresentation={props.onCloneDiscussionRequested}
        onBeginPresentation={props.onBeginPresentation}
        onEndPresentation={props.onEndPresentation}
        onDeletePresentation={props.onDeletePresentation}
      />
      <div ref={sectionRulerRef} style={{ width: '100%' }} />
      <BwjAnnouncementBanner
        invokeShowAnnouncement={props.invokeShowAnnouncement}
        closeDialogButtonText={xlate('Close')}
        ariaCloseBannerMessage={xlate('Dismiss announcement banner')}
        ariaShowFullMessageDialog={xlate('Display full announcement')}
      />
      <BwjPageHeader
        title={xlate('My Discussion Plans')}
        titleIconName={AppRoutes.myPresentations.icon}
        actionButtons={[{
          id: 'btnCreate',
          name: xlate('Create new discussion plan'),
          iconName: 'Add',
          onClick: props.onCreatePresentation,
        }]}
      />
      <BwjPageContent
        contentDescription='primary content (no subcontent)'
        style={{ width: '100%', boxSizing: 'border-box', }}
      >
        {props.presentations.length > 1 &&
        <BwjGrid>
          <Row bottomSpacing={Spacing.Medium}>
            <Col small={12} medium={6} large={4} xLarge={3}>
              <BwjSingleSelectField
                id={'ddwnDisplayOrder'}
                required={false}
                label={xlate('Display order')}
                onUpdate={option => props.onOrderingOptionUpdate(option ?? props.orderingOptions[0])}
                input={props.orderingOptionInput}
                options={props.orderingOptions}
                optionDisplayKey={'description'}
                optionValueKey={'value'}
              />
            </Col>
          </Row>
        </BwjGrid>}
        {props.presentations.map(listing => <PresentationListingComponent
          key={listing.presentation.id}
          presentation={listing.presentation}
          menuState={listing.menuState}
          onWillOpenContextMenu={onWillOpenContextMenu}
          onContextMenuOpened={props.onContextMenuOpened}
          onContextMenuClosed={props.onContextMenuClosed}
          onEditPresentation={props.onEditPresentation}
          onClonePresentation={props.onCloneDiscussionRequested}
          onBeginPresentation={props.onBeginPresentation}
          onEndPresentation={props.onEndPresentation}
          onDeletePresentation={props.onDeletePresentation}
          onWillCloseContextMenu={props.onWillCloseContextMenu}
        />)}
      </BwjPageContent>
    </>
  );
};
